import React from 'react';
import colors from 'src/utils/colors';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { TooltipIcon } from 'src/components/SvgIcons/SvgIcons';
import {
  voiceTypes,
  toneTypes
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { LabelledDropdown } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/StyledDropdown';
import { arrayToOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/utils';
import { useFormContext, useWatch, FieldValues, Path } from 'react-hook-form';

interface DominantSectionsProps<T extends FieldValues> {
  voiceFieldName: Path<T>;
  toneFieldName: Path<T>;
  readOnly?: boolean;
  onVoiceTooltipClick?: () => void;
  onToneTooltipClick?: () => void;
}

// Common dropdown styles for both Voice and Tone
const getDropdownStyles = (disabled: boolean) => ({
  buttonSx: {
    height: '32px',
    width: disabled ? 'fit-content' : '130px',
    minWidth: disabled ? 'unset' : undefined,
    justifyContent: 'space-between',
    textAlign: 'left',
    paddingLeft: '12px',
    paddingRight: disabled ? '4px' : '12px',
    '& .MuiTypography-root': {
      fontWeight: '400 !important',
      fontSize: '14px',
      lineHeight: '20px',
      textAlign: 'left',
      flex: 1
    }
  }
});

const DominantCard = <T extends FieldValues>({
  title,
  description,
  options,
  label,
  fieldName,
  defaultValue,
  readOnly,
  onTooltipClick }: {
  title: string;
  description: string;
  options: Array<{ id: string; label: string }>;
  label: string;
  fieldName: Path<T>;
  defaultValue: string;
  readOnly: boolean;
  onTooltipClick?: () => void;
}) => (
  <Flex flexDirection="column" sx={{ width: '400px' }}>
    <Flex flexDirection="column" gap={4} sx={{ marginBottom: '16px' }}>
      <Flex alignItems="center" sx={{ gap: '8px' }}>
        <Text sx={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.08px', lineHeight: '24px' }}>{title}</Text>
        <TooltipIcon width={16} height={16} onClick={onTooltipClick} style={{ cursor: 'pointer' }} color={colors.actualSpend}/>
      </Flex>
      <Text variant="body1" sx={{ lineHeight: '24px' }}>{description}</Text>
    </Flex>
    <LabelledDropdown<T>
      options={options}
      label={label}
      name={fieldName}
      defaultValue={defaultValue}
      disabled={readOnly}
      stacklineButtonProps={getDropdownStyles(readOnly)}
    />
  </Flex>
);

export const DominantSections = <T extends FieldValues>({
  voiceFieldName,
  toneFieldName,
  readOnly = false,
  onVoiceTooltipClick,
  onToneTooltipClick
}: DominantSectionsProps<T>) => {
  const { control } = useFormContext<T>();

  // Watch voice and tone values for the dropdowns
  const voiceType = useWatch({ control, name: voiceFieldName });
  const toneType = useWatch({ control, name: toneFieldName });

  return (
    <Flex gap={64}>
      <DominantCard<T>
        title="Dominant Voice"
        description="Select the voice for this profile that best reflects your brand's personality and values."
        options={arrayToOptions(voiceTypes)}
        label="Voice"
        fieldName={voiceFieldName}
        defaultValue={voiceType || 'Informative'}
        readOnly={readOnly}
        onTooltipClick={onVoiceTooltipClick}
      />

      <DominantCard<T>
        title="Dominant Tone"
        description="Select the tone for this profile that best reflects how your brand communicates."
        options={arrayToOptions(toneTypes)}
        label="Tone"
        fieldName={toneFieldName}
        defaultValue={toneType || 'Friendly'}
        readOnly={readOnly}
        onTooltipClick={onToneTooltipClick}
      />
    </Flex>
  );
};
